import React, { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { createSupportTicket } from "../../services/supportService";
import { CreateSupportTicketResponse } from "../../proto/api_pb";
import Mixpanel from "../../utils/mixpanel";
import { featureList } from "../SubscriptionPlans/helper";
import { APP_COLOR } from "../../theme";
import { Button, Dialog } from "../ui";
import { EnterpriseFeature, ENTERPRISE_FEATURE_COPY, buildEnterpriseInquiryTicket } from "./enterpriseUpsell";

const PREVIEW_SRC = "/assets/enterprise/enterprise-preview.png";

interface EnterpriseUpsellModalProps {
  open: boolean;
  feature: EnterpriseFeature;
  userId: string;
  userEmail: string;
  onClose: () => void;
}

const EnterpriseUpsellModal: React.FC<EnterpriseUpsellModalProps> = ({
  open,
  feature,
  userId,
  userEmail,
  onClose,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const [email, setEmail] = useState(userEmail);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    setEmail(userEmail);
  }, [userEmail]);

  const copy = ENTERPRISE_FEATURE_COPY[feature];

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.warning("Please enter an email so we can reach you");
      return;
    }
    setLoading(true);
    try {
      const token = await getValidAccessToken();
      const ticket = buildEnterpriseInquiryTicket(feature, email, message);
      const response = await createSupportTicket(
        token,
        userId,
        ticket.category,
        ticket.subject,
        ticket.description,
        ticket.customCategory,
        ticket.screenshotUrl,
      );
      if (response instanceof CreateSupportTicketResponse) {
        Mixpanel.track("enterprise_inquiry_submitted", { feature });
        toast.success("Thanks — we'll be in touch!");
        onClose();
      } else {
        Sentry.captureMessage("enterprise_inquiry_submit_failed", {
          level: "error",
          extra: { userId, feature, response },
        });
        toast.error("Couldn't send your request. Please try again.");
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: { userId, feature, context: "enterprise_inquiry_submit" },
      });
      toast.error("Couldn't send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <Dialog.Content size="md" className="p-0 sm:p-0" aria-describedby={undefined}>
        {showPreview && (
          <Box
            component="img"
            src={PREVIEW_SRC}
            alt={`${feature} preview`}
            onError={() => setShowPreview(false)}
            sx={{ width: "100%", display: "block" }}
          />
        )}
        <Box sx={{ p: 3 }}>
          <Typography sx={{ color: APP_COLOR, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em" }}>
            ENTERPRISE
          </Typography>
          <Dialog.Header title={copy.headline} icon={<LockOutlinedIcon />} className="mb-3 mt-1.5" />
          <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2, color: "#889096", fontSize: 13.5 }}>
            {featureList.map((f) => (
              <Box component="li" key={f} sx={{ mb: 0.5 }}>
                {f}
              </Box>
            ))}
          </Box>
          <TextField
            label="Work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            sx={{ mb: 2 }}
          />
          <Button variant="primary" onClick={handleSubmit} disabled={loading} fullWidth>
            {loading ? "Sending…" : "Request access"}
          </Button>
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default EnterpriseUpsellModal;
